import { Component, Input, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { MatMenu, MatMenuItem, MatMenuTrigger } from "@angular/material/menu";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Subject, takeUntil } from "rxjs";
import { HttpErrorResponse } from "@angular/common/http";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { School } from "../../../models/school.model";
import { UserService } from "../../../../services/user-service.service";
import { MainMenuService } from "../../../../services/main-menu.service";
import { RuntimeConfigService } from "../../../../services/runtime-config.service";
import { PortalLinkService } from "../../../../services/portal-link.service";
import { PhoneNumberFormater } from "../../../../helpers/phone-number-formater";
import { SchoolsService } from "../schools.service";
import { SchoolMealsService } from "../school-meals.service";
import { SchoolWebsiteService } from "../school-website.service";
import { AdaptiveContainerService } from "../adaptive-container/adaptive-container.service";
import { SchoolResourcesModalComponent } from "../modal/school-resources-modal/school-resources-modal.component";
import { GradeLevelOption } from "../grade-levels";
import { VolunteerRecordStatus } from "../../student/models/VolunteerRecordStatus";
import { PARENT_RELATIONS_PHONE_DISPLAY, PARENT_RELATIONS_PHONE_TEL } from "../nha-contact-constants";
import { RtI18nDirective } from "../runtime-translation.directive";

/**
 * One school's info card (address/phone menus, website + lunch-menu links, and the
 * Directory/Calendar/Resources/Volunteer pills), shared by the main-menu sidebar and the
 * student dashboard. Pass the school in; the card resolves its own CMS links (cached for
 * hours by the services) and the guardian's volunteer status.
 */
@Component({
    selector: 'app-school-card',
    standalone: true,
    templateUrl: './school-card.component.html',
    styleUrl: './school-card.component.scss',
    imports: [MatMenuTrigger, MatMenu, MatMenuItem, RtI18nDirective]
})
export class SchoolCardComponent implements OnDestroy {

  readonly parentRelationsPhoneDisplay = PARENT_RELATIONS_PHONE_DISPLAY;
  readonly parentRelationsPhoneTel = PARENT_RELATIONS_PHONE_TEL;

  private destroy$ = new Subject<void>();
  private _school!: School;

  /** CMS links; visibility is purely "did the CMS return a URL", matching the Scholar portal. */
  public lunchMenuUrl: string | null = null;
  public websiteUrl: string | null = null;

  /** Guardian's volunteer record status for this school (null until loaded / non-guardians). */
  public volunteerStatus: VolunteerRecordStatus | null = null;

  /** Grade options for the resources dialog's grade dropdown (see gradeOptionsForSchool). */
  @Input() gradeOptions: GradeLevelOption[] = [];

  @Input({ required: true }) set school(value: School) {
    const changed = this._school?.Id !== value?.Id;
    this._school = value;
    if (changed && value) {
      this.lunchMenuUrl = null;
      this.websiteUrl = null;
      this.volunteerStatus = null;
      this.loadCardLinks();
    }
  }

  get school(): School {
    return this._school;
  }

  constructor(
    private router: Router,
    private snackBar: MatSnackBar,
    private userService: UserService,
    private mainMenuService: MainMenuService,
    private runtimeConfig: RuntimeConfigService,
    public portalLink: PortalLinkService,
    private schoolsService: SchoolsService,
    private schoolMealsService: SchoolMealsService,
    private schoolWebsiteService: SchoolWebsiteService,
    private adaptiveDialog: AdaptiveContainerService
  ) { }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isGuardian(): boolean {
    return this.userService.CurrentUserProfile?.UserType === 'Guardian';
  }

  get canAccessDirectory(): boolean {
    return this.userService.CurrentUserProfile?.Roles.some(r => r.RoleName === 'AppAccess_Directory') ?? false;
  }

  get canAccessCalendar(): boolean {
    return this.userService.CurrentUserProfile?.Roles.some(r => r.RoleName === 'AppAccess_Calendar') ?? false;
  }

  /** The Scholar parent portal's volunteer form (portal base URL is per-environment config). */
  get volunteerFormUrl(): string {
    return this.runtimeConfig.portalUrl('volunteer');
  }

  /**
   * Resolves the card's lunch-menu and website links from the public nhaschools.com CMS
   * (the services cache for hours), plus the guardian's volunteer status. Fail-soft all
   * around — a missing link just doesn't render.
   */
  private loadCardLinks(): void {
    // The CMS is keyed by the NHA/SIS school ID (SchoolEID), not our local school Id.
    // Non-positive EIDs (e.g. -1 on non-campus staff locations) aren't real CMS schools.
    if (this.school.SchoolEID > 0) {
      this.schoolMealsService.getMealsMenuUrl(this.school.SchoolEID).pipe(
        takeUntil(this.destroy$)
      ).subscribe(url => {
        this.lunchMenuUrl = url || null;
      });

      this.schoolWebsiteService.getSchoolWebsiteUrl(this.school.SchoolEID).pipe(
        takeUntil(this.destroy$)
      ).subscribe(url => {
        this.websiteUrl = url || null;
      });
    }

    this.loadVolunteerRecordStatus();
  }

  /**
   * Marks the Volunteer pill with a check when the guardian already has a volunteer record
   * for the current academic year (the server resolves the year). Guardian-only; fail-soft —
   * on error the pill just keeps its default icon.
   */
  private loadVolunteerRecordStatus(): void {
    if (this.userService.CurrentUserProfile?.UserType !== 'Guardian') return;

    this.mainMenuService.getVolunteerRecordStatusAsync(this.school.Id).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: status => {
        this.volunteerStatus = status;
      },
      error: (error: HttpErrorResponse) => {
        console.error('Error loading volunteer record status for school', this.school.Id, error);
      }
    });
  }

  /** The school's full address as a single line, for the maps link and the clipboard. */
  private get schoolAddress(): string {
    return `${this.school.Address}, ${this.school.City}, ${this.school.StateCode} ${this.school.ZipCode}`;
  }

  openSchoolInGoogleMaps(): void {
    const query = encodeURIComponent(this.schoolAddress);
    window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank');
  }

  copySchoolAddress(): void {
    navigator.clipboard?.writeText(this.schoolAddress).then(() => {
      this.snackBar.open(`Address Copied: ${this.schoolAddress}`, undefined, { duration: 3000 });
    }).catch(err => {
      console.error('Failed to copy address:', err);
    });
  }

  formatSchoolPhone(): string {
    return PhoneNumberFormater.formatPhoneNumber(this.school.Phone) ?? this.school.Phone;
  }

  /** Digits-only number for the tel: link (the Call menu item is a real tel: anchor, so the
   *  global click interceptor in app.component handles analytics and the desktop-web fallback). */
  getSchoolPhoneDigits(): string {
    return this.school.Phone.replace(/\D/g, '');
  }

  copySchoolPhone(): void {
    const phone = this.formatSchoolPhone();
    navigator.clipboard?.writeText(phone).then(() => {
      this.snackBar.open(`Phone Number Copied: ${phone}`, undefined, { duration: 3000 });
    }).catch(err => {
      console.error('Failed to copy phone number:', err);
    });
  }

  copyAttendanceEmail() {
    const email = 'pvaattendance@nhaschools.com';
    navigator.clipboard?.writeText(email).then(() => {
      this.snackBar.open(`Email Copied: ${email}`, undefined, { duration: 3000 });
    }).catch(err => {
      console.error('Failed to copy email:', err);
    });
  }

  copyParentRelationsPhone(): void {
    navigator.clipboard?.writeText(this.parentRelationsPhoneDisplay).then(() => {
      this.snackBar.open(`Phone Number Copied: ${this.parentRelationsPhoneDisplay}`, undefined, { duration: 3000 });
    }).catch(err => {
      console.error('Failed to copy phone number:', err);
    });
  }

  /** Opens the school's resources dialog (links + documents) from its card's Resources pill. */
  openResources(): void {
    Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
    this.adaptiveDialog.open(SchoolResourcesModalComponent, {
      width: '480px',
      data: { school: this.school, gradeOptions: this.gradeOptions }
    });
  }

  /**
   * Opens an app from the card, scoped to this school: persists it as the shared
   * school-selector selection first (the same storage app-school-selector reads on init),
   * so the app opens already filtered to this school.
   */
  async openAppForSchool(appPath: 'directory' | 'calendar'): Promise<void> {
    Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
    await this.schoolsService.selectSchool(this.school);
    this.router.navigate(['/apps/' + appPath]);
  }
}
